// React
import { useMemo, useState } from "react"

// UI
import { RealtimeProduction } from "@/components/visx/RealtimeProduction"

// Query
import { useFinanceGraphDailyProductionQuery } from "@/api/graphql"

// Hooks
import { useToday } from "@/hooks/useToday"
import { useCurrentFinanceProjectId } from "../hooks/useCurrentFinanceProjectId"

// Date
import { DateTime } from "@/lib/dates"
import { apiDateFormat } from "@/constants/constants"

// Graphs
import { useAreaGraphContext } from "@/components/graphs/types/area/AreaGraphContext"
import { AreaGraphBeforeSunrise } from "@/components/graphs/types/area/AreaGraphBeforeSunrise"
import { ProductionDailyNoData } from "@/components/graphs/types/area/ProductionDailyNoData"

function useFinanceGraphDailyProductionQueryChartData() {
	const { data } = useFinanceGraphDailyProductionQueryData()

	return useMemo(() => {
		return (
			data?.me?.finance_projects?.results?.[0]?.overall_statistics?.production_for_day?.production_data?.map(
				(item) => ({
					x: item?.date ? DateTime.fromISO(item.date).toMillis() : 0,
					y: item?.production ? parseFloat(item.production) : 0,
				}),
			) ?? []
		)
	}, [data?.me?.finance_projects])
}

function useHasNoData() {
	const chartData = useFinanceGraphDailyProductionQueryChartData()
	const { data } = useFinanceGraphDailyProductionQueryData()

	return (
		!chartData?.length ||
		// does the project have no data? we can check this by looking through
		// the array and seeing if all of the values have null or "0".
		// since the api can return a full array, but full of null values or "0"
		data?.me?.finance_projects?.results?.[0]?.overall_statistics?.production_for_day?.production_data?.every(
			(value) => value?.production === null || value?.production === "0",
		)
	)
}

function useFinanceGraphDailyProductionQueryData() {
	const id = useCurrentFinanceProjectId()
	const { date } = useAreaGraphContext()

	return useFinanceGraphDailyProductionQuery({
		id: String(id),
		date: DateTime.fromJSDate(date).toFormat(apiDateFormat),
	})
}

export function FinanceGraphProductionDaily() {
	const today = useToday()
	const [, set] = useState(280)
	const { date, sunset, isBeforeSunrise } = useAreaGraphContext()

	const { data } = useFinanceGraphDailyProductionQueryData()
	const chartData = useFinanceGraphDailyProductionQueryChartData()

	const totalValue = data?.me?.finance_projects?.results?.[0]
		?.overall_statistics?.production_for_day?.total_production
		? parseFloat(
				data.me.finance_projects.results?.[0]?.overall_statistics
					.production_for_day.total_production,
			)
		: 0

	const hasNoData = useHasNoData()

	if (isBeforeSunrise) {
		return <AreaGraphBeforeSunrise />
	}

	if (hasNoData) {
		return (
			<ProductionDailyNoData
				variant={
					DateTime.fromJSDate(date).hasSame(today, "day")
						? "today"
						: "past"
				}
			/>
		)
	}

	const values = {
		sunset: DateTime.fromMillis(
			chartData[chartData.length - 1]?.x || sunset,
		).toFormat("HH:mm"),
		sunrise: DateTime.fromMillis(chartData[0]?.x || sunset).toFormat(
			"HH:mm",
		),
		now: DateTime.fromJSDate(date).toFormat("HH:mm"),
	}

	return (
		<RealtimeProduction
			key={date.toDateString()}
			margin={{
				top: 24,
				bottom: 48,
				left: 24,
				right: 24,
			}}
			onTooltip={(data) => {
				set(parseFloat(data.y.toFixed(2)))
			}}
			data={chartData}
			sunrise={values.sunrise}
			sunset={values.sunset}
			totalValue={totalValue}
		/>
	)
}
